<template>
  <div class="CourtImage">
    <BlurhashCanvas
      v-if="court.photo_blurhash"
      v-model="court.photo_blurhash"
      class="CourtImage__placeholder"
    />
    <div
      v-else
      class="CourtImage__placeholder"
    >
      <svg
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        :viewBox="`0 0 ${imageWidth} ${imageHeight}`"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M77 144H0V70H77V144ZM2 142H75V72H2V142Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M76 78C92.0163 78 105 90.9837 105 107C105 123.016 92.0163 136 76 136C59.9837 136 47 123.016 47 107C47 90.9837 59.9837 78 76 78ZM76 80C61.0883 80 49 92.0883 49 107C49 121.912 61.0883 134 76 134C90.9117 134 103 121.912 103 107C103 92.0883 90.9117 80 76 80Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M40 32.0059C80.9605 32.5411 114 65.9125 114 107C114 148.088 80.9604 181.458 40 181.993V182H0V32H40V32.0059ZM2 180H39.4619L39.9736 179.993C79.8412 179.472 112 146.992 112 107C112 67.0083 79.8412 34.5268 39.9736 34.0059L39.5352 34H2V180Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M352 144H275V135.98C259.447 135.453 247 122.682 247 107C247 91.3184 259.447 78.5456 275 78.0186V70H352V144ZM277 78.0186C292.553 78.5456 305 91.3184 305 107C305 122.682 292.553 135.453 277 135.98V142H350V72H277V78.0186ZM275 80.0195C260.552 80.5456 249 92.4231 249 107C249 121.577 260.552 133.453 275 133.979V80.0195ZM277 133.979C291.448 133.453 303 121.577 303 107C303 92.4231 291.448 80.5456 277 80.0195V133.979Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M176 78C192.016 78 205 90.9837 205 107C205 123.016 192.016 136 176 136C159.984 136 147 123.016 147 107C147 90.9837 159.984 78 176 78ZM176 80C161.088 80 149 92.0883 149 107C149 121.912 161.088 134 176 134C190.912 134 203 121.912 203 107C203 92.0883 190.912 80 176 80Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M9 106H11.0723C11.5576 102.608 14.4736 100 18 100C21.866 100 25 103.134 25 107C25 110.866 21.866 114 18 114C14.4736 114 11.5576 111.392 11.0723 108H9V125H7V89H9V106ZM18 102C15.2386 102 13 104.239 13 107C13 109.761 15.2386 112 18 112C20.7614 112 23 109.761 23 107C23 104.239 20.7614 102 18 102Z"
        />
        <path d="M177 214H175V0H177V214Z" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M352 214H0V0H352V214ZM2 212H111V200H113V212H239V200H241V212H350V182H312V181.993C271.04 181.458 238 148.088 238 107C238 65.9125 271.04 32.5411 312 32.0059V32H350V2H241V14H239V2H113V14H111V2H2V212ZM312.465 34L312.026 34.0059C272.159 34.5268 240 67.0083 240 107C240 146.992 272.159 179.472 312.026 179.993L312.538 180H350V34H312.465Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M343 106H340.928C340.442 102.608 337.526 100 334 100C330.134 100 327 103.134 327 107C327 110.866 330.134 114 334 114C337.526 114 340.442 111.392 340.928 108H343V125H345V89H343V106ZM334 102C336.761 102 339 104.239 339 107C339 109.761 336.761 112 334 112C331.239 112 329 109.761 329 107C329 104.239 331.239 102 334 102Z"
        />
      </svg>
    </div>
    <img
      v-if="imageUrl"
      class="CourtImage__image"
      loading="lazy"
      :width="imageWidth"
      :height="imageHeight"
      :src="imageUrl"
      :alt="`${court.name} court image`"
      :data-is-loaded="isImageLoaded"
      @loadstart="isImageLoaded = false"
      @load="isImageLoaded = true"
    >
  </div>
</template>

<script lang="ts" setup>
import { supabaseClient } from "@/lib/database/supabase";
import { CourtSummary } from "@shared/lib/database/query.types";
import { ref, watch } from "vue";
import BlurhashCanvas from "./BlurhashCanvas.vue";

const court = defineModel<CourtSummary>({
  required: true,
});

const imageUrl = ref<string | null>(null);
const isImageLoaded = ref(false);
const imageWidth = 352;
const imageHeight = 214;

watch(() => court.value.photo_storage_path, (path) => {
  imageUrl.value = path ? getPhotoUrl(path) : null;
}, {
  immediate: true,
});

function getPhotoUrl (path: string): string {
  if (path.startsWith("/")) return path;

  const {
    data: urlData,
  } = supabaseClient.storage
    .from("court-photos")
    .getPublicUrl(path, {
      transform: {
        width: 800,
        height: 450,
        resize: "cover",
      },
    });

  return urlData.publicUrl;
}
</script>

<style lang="scss" scoped>
.CourtImage {
  --margin: 12;
  position: relative;
  isolation: isolate;

  width: 100%;
  aspect-ratio: calc(v-bind(imageWidth) + 2 * var(--margin)) / calc(v-bind(imageHeight) + 2 * var(--margin));
}

.CourtImage__image {
  position: absolute;

  display: block;
  width: 100%;
  height: 100%;

  object-fit: cover;
  object-position: center;

  transition: opacity 350ms;

  &[data-is-loaded="false"] {
    opacity: 0;
  }
}

.CourtImage__placeholder {
  position: absolute;
  z-index: -1;

  width: 100%;
  height: 100%;

  background-color: var(--themeColorPrimary);

  svg {
    width: calc(100% - 2 * var(--margin) * 1px);
    height: calc(100% - 2 * var(--margin) * 1px);
    margin: calc(var(--margin) * 1px);
  }

  path {
    fill: var(--themeColorPrimaryForeground);
  }
}
</style>
